import React, { useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  GET_BARE_SENT_EMAILS,
  GET_BARE_SENT_EMAILS_SEARCH,
} from "../../../../utils/GraphQL/queries";
import "./sentEmailsPage.css";
import { useMediaQuery } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import EmailRow from "../../adminComponents/EmailRow/EmailRow";
import SentEmailModal from "../../adminComponents/SentEmailModal/SentEmailModal";
import Auth from "../../../../utils/auth";
import { useSSEListener } from "../../../../utils/SSE/SSEContext";
import { hasRole } from '../../../../utils/permissions';
import { lockBodyScroll, releaseBodyScroll } from "../../../../utils/bodyScrollLock";
import { PAGE_SIZE, searchCountLines, useEmailList } from "../../../../utils/emailSearch";

const NO_EMAILS = [];

const SentEmailsPage = () => {
  const userProfile = Auth.getProfile()?.data;
  const userPermission = userProfile?.permission;
  const userEmailAddress = userProfile?.userEmailAddress;
  const [showMyInbox, setShowMyInbox] = useState(true);
  const [sentEmailId, setSentEmailId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [page, setPage] = useState(1);
  // The list runs through client.query, not useLazyQuery: a lazy query's
  // promise settles only when its observable completes (useQuery.js
  // executeQuery), and executed from the list's effect it was seen never to
  // settle in the jsdom harness — the search would never land. client.query
  // is one request, one promise.
  const client = useApolloClient();
  /* 900, not 600 — the labels collide well before the phone breakpoint
     and this app is used in a half-screen desktop window. Matches the
     inbox; see the note there on why it is not `collapseToIcon`. */
  const smaller900 = useMediaQuery("(max-width:900px)");

  const mailboxVars = (mySent) =>
    mySent && userEmailAddress
      ? { userEmailAddress }
      : !mySent
      ? { showAll: true }
      : {};

  // The same list model as the inbox (utils/emailSearch.js, plan route R5):
  // one key, one refresh(); a search is 3+ letters, newest 200, one list.
  const list = useEmailList({
    text: openSearch ? searchInput : "",
    view: "sent",
    mailbox: showMyInbox,
    page,
    runNormal: async ({ mailbox, page: p }) => {
      const { data, error } = await client.query({
        query: GET_BARE_SENT_EMAILS,
        variables: { page: String(p), ...mailboxVars(mailbox) },
        fetchPolicy: "network-only",
      });
      return {
        emails: data?.getBareSentEmails?.emails,
        totalDoc: data?.getBareSentEmails?.totalDoc,
        error,
      };
    },
    runSearch: async ({ term, mailbox }) => {
      const { data, error } = await client.query({
        query: GET_BARE_SENT_EMAILS_SEARCH,
        variables: { searchTerm: term, ...mailboxVars(mailbox) },
        fetchPolicy: "network-only",
      });
      return {
        emails: data?.getBareSentEmails?.emails,
        totalDoc: data?.getBareSentEmails?.totalDoc,
        error,
      };
    },
  });
  const refresh = list.refresh;
  const searching = list.mode === "search";
  // A failed normal fetch keeps the last rows only when they are THIS screen's
  // (same mailbox and page — D27); otherwise "Emails didn't load".
  const normalFailedKeepsRows =
    list.mode === "none" && list.state === "failed" && list.lastNormalIsCurrent;
  const displayed =
    list.state === "answered"
      ? list.answer.emails
      : normalFailedKeepsRows
      ? list.lastNormal.emails
      : NO_EMAILS;
  // The count: this page's answer, or the last answer of the SAME list (any
  // page) while another page loads or after it fails — so a page that will
  // not load can be paged away from; never another mailbox's numbers (D27).
  const normalCount =
    list.mode === "none"
      ? list.state === "answered"
        ? list.answer
        : list.lastNormalSameList
        ? list.lastNormal
        : null
      : null;
  const totalDoc = Number(normalCount?.totalDoc) || 0;
  const highestPage = Math.max(1, Math.ceil(totalDoc / PAGE_SIZE));
  const pagerBusy = list.state === "pending";

  // SSE: silently refresh the CURRENT key (normal list or search) when another
  // user triggers an email_update — through a ref, the handler is memoised once.
  // Owner-mail events (registry §5.1c) carry `scope: "owner"`. On this page
  // such mail shows only in "my sent" (for whoever sent it) and in the admin
  // "all" view, so any other view is left alone.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const showMyInboxRef = useRef(showMyInbox);
  showMyInboxRef.current = showMyInbox;
  const isAdminRef = useRef(false);
  isAdminRef.current = hasRole(userPermission, "admin");
  const handleEmailSSEUpdate = useCallback((data) => {
    if (data?.scope === "owner" && !showMyInboxRef.current && !isAdminRef.current) return;
    refreshRef.current?.();
  }, []);
  useSSEListener("email_update", handleEmailSSEUpdate);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  /* Lock + release in the cleanup, so the pair is balanced by construction.
     The old shape released on every render where the modal was shut —
     including before it had ever opened — which drives a shared count
     negative. See utils/bodyScrollLock.js. */
  useEffect(() => {
    if (!emailModalOpen) return undefined;
    lockBodyScroll();
    return () => releaseBodyScroll();
  }, [emailModalOpen]);

  const openEmail = (id) => {
    setSentEmailId(id);
    setEmailModalOpen(true);
  };
  const closeEmail = () => {
    setSentEmailId("");
    setEmailModalOpen(false);
    refresh();
  };
  const handlePage = (direction) => {
    if (direction === "up") setPage((p) => Math.min(p + 1, highestPage));
    if (direction === "down") setPage((p) => Math.max(p - 1, 1));
  };
  const loadingHolders = () => {
    return Array.from({ length: 15 }).map((_, index) => (
      <EmailRow key={index + Date.now()} loading />
    ));
  };

  return (
    <div className="sent-email-page">
      {emailModalOpen && (
        <SentEmailModal closeEmail={closeEmail} sentId={sentEmailId} />
      )}
      <div
        className={
          emailModalOpen
            ? "sent-email-page-container bdr-section-panel"
            : "sent-email-page-container bdr-section-panel sent-email-page-filter"
        }
      >
        {/* The blue band: title and every control. Opaque on purpose —
            `.bdr-section-panel-hdr` has to occlude what scrolls under it. */}
        <div className="bdr-section-panel-hdr sent-email-page-hdr">
          <h2 className="bdr-section-panel-title">Viewing Email History</h2>
        <div className="sent-email-actions-button-box">
          {hasRole(userPermission, 'admin') && (
            <BdrButton
              variant="action"
              icon={<SendIcon />}
              iconOnly={smaller900}
              onClick={() => setShowMyInbox((prev) => !prev)}
              title={
                showMyInbox
                  ? "Show All"
                  : userEmailAddress
                  ? "Show My Sent"
                  : "Hide Private"
              }
            >
              {showMyInbox ? "Show All" : userEmailAddress ? "Show My Sent" : "Hide Private"}
            </BdrButton>
          )}
          <BdrButton
            variant="action"
            icon={<SearchIcon />}
            iconOnly={smaller900}
            onClick={() => {
              if (openSearch) {
                setSearchInput("");
              }
              setOpenSearch((prev) => !prev);
            }}
            title={openSearch ? "Close Search" : "Search Emails"}
          >
            {openSearch ? "Close Search" : "Search Emails"}
          </BdrButton>
        </div>
        {openSearch && (
          <div className="sent-email-page-search-box">
            <div className="sent-email-page-search-input">
              {/* The debounce spinner was wired through MUI's
                  `InputProps.endAdornment`, which BdrInput does not read — it
                  spread onto the DOM as an unknown attribute, so this search
                  showed no loading state at all. `loading` is the real prop. */}
              <BdrInput
                type="text"
                label="Search"
                name="to"
                inputMode="text"
                autoComplete="off"
                autoFocus
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                loading={searching && list.state === "pending"}
              />
            </div>
          </div>
        )}
        <div className="email-page-page-box" role="status">
          {list.state === "answered" && searching &&
            searchCountLines(list.answer.totalDoc).map((line) => (
              <span key={line} className="email-page-count-line">{line}</span>
            ))}
          {list.mode === "none" && normalCount && totalDoc > 0 && (
            <>
              <span style={{ margin: "0px 5px" }}>
                {`${totalDoc} ${totalDoc > 1 ? "Emails" : "Email"} - Page ${page} of ${highestPage}`}
              </span>
              {page !== 1 && (
                <div className="email-page-page-control-button-box">
                  <button
                    disabled={pagerBusy}
                    className="email-page-page-control-button"
                    aria-label="Previous page"
                    onClick={() => handlePage("down")}
                  >
                    <strong>-</strong>
                  </button>
                </div>
              )}
              {page < highestPage && (
                <div className="email-page-page-control-button-box">
                  <button
                    disabled={pagerBusy}
                    className="email-page-page-control-button"
                    aria-label="Next page"
                    onClick={() => handlePage("up")}
                  >
                    <strong>+</strong>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        </div>

        {/* The gray body the blue rows sit on. */}
        <div className="bdr-section-panel-body sent-email-page-body">
        {list.state === "pending" && loadingHolders()}
        {list.state === "short" && (
          <p className="bdr-empty">Type at least 3 letters to search</p>
        )}
        {list.state === "failed" && (searching || !normalFailedKeepsRows) && (
          <div className="bdr-alert bdr-alert--danger email-search-failed" role="alert">
            <span>{searching ? "Search didn't load — try again." : "Emails didn't load — try again."}</span>
            <BdrButton variant="action-solid" onClick={() => refresh()}>
              Try again
            </BdrButton>
          </div>
        )}
        {displayed.length > 0 ? (
          displayed.map((email, index) => {
            /* Date and address parsing live in `EmailRow` now. */
            return (
                <EmailRow
                key={"emails-number:" + email?.id + index}
                email={email}
                /* Leads with the RECIPIENT and draws no status lane: a sent
                   message has neither unread nor flag state, and the sender is
                   the office address on nearly every row. */
                kind="sent"
                onOpen={() => openEmail(email?.id)}
              />
            );
          })
        ) : (
          /* The shared `.bdr-empty` recipe — see the note on the inbox's
             copy. `.page-header` rendered this as a gradient card that read
             as a button in the middle of the list. */
          list.state === "answered" && (
            <p className="bdr-empty">
              {searching ? "No sent emails match that search." : "Nothing sent yet."}
            </p>
          )
        )}
        </div>
      </div>
    </div>
  );
};
export default SentEmailsPage;
